// Debt Service - Updated January 10, 2025: Added wallet debt integration functionality
import axiosInstance from '../utils/axiosConfig';

// All requests go through axiosInstance with baseURL = VITE_API_URL

// Cache store for API responses - separate caches for partner mode and non-partner mode
const cache = {
  debts: null,
  debtStats: null,
  lastFetchTime: 0,
  cacheExpiry: 60000, // Cache expires after 1 minute
  // Partner mode cache
  partnerDebts: null,
  partnerDebtStats: null,
  partnerLastFetchTime: 0
};

/**
 * Debt Service
 * 
 * Handles all debt-related operations and data fetching
 */
const debtService = {
  /**
   * Clear all caches (useful when switching between partner/personal modes)
   */
  clearCache() {
    cache.debts = null;
    cache.debtStats = null;
    cache.partnerDebts = null;
    cache.partnerDebtStats = null;
    cache.lastFetchTime = 0;
    cache.partnerLastFetchTime = 0;
    console.log('🗑️ Debt service cache cleared');
  },

  /**
   * Get authentication header with JWT token
   * @returns {Object} Headers object with Authorization
   */
  getAuthHeader() {
    const token = localStorage.getItem('token');
    return {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    };
  },

  /**
   * Get all debts for the current user with caching
   * @returns {Promise} Promise with debts data
   */
  async getAllDebts(isPartnerMode = false, isOurMode = false) {
    try {
      // Check if we have cached data and if it's still valid - use mode-specific cache
      const now = Date.now();
      let cachedData, lastFetch;
      const mode = isOurMode ? 'our' : (isPartnerMode ? 'partner' : 'personal');
      
      if (isOurMode) {
        cachedData = cache.ourDebts;
        lastFetch = cache.ourLastFetchTime;
      } else if (isPartnerMode) {
        cachedData = cache.partnerDebts;
        lastFetch = cache.partnerLastFetchTime;
      } else {
        cachedData = cache.debts;
        lastFetch = cache.lastFetchTime;
      }
      
      const cacheAge = now - lastFetch;
      const hasValidCache = cachedData && (cacheAge < cache.cacheExpiry);
      
      if (hasValidCache) {
        console.log(`Using cached debts data (${mode})`);
        return cachedData;
      }

      // Build URL with appropriate query parameter
      let url = `/api/cashflow/debts`;
      if (isPartnerMode) {
        url += `?partner=true`;
      } else if (isOurMode) {
        url += `?includePartner=true`;
      }
      
      console.log('🔍 debtService.getAllDebts - URL:', url, 'mode:', mode);
      const response = await axiosInstance.get(url, this.getAuthHeader());
      
      console.log('🏦 Manual debts API response:', response.data);
      
      // Extract the actual debts array from the server response
      const debtsData = response.data?.data || response.data || [];
      const debts = Array.isArray(debtsData) ? debtsData : [];
      
      console.log('🏦 Extracted manual debts:', debts.length);
      
      // Update mode-specific cache
      if (isPartnerMode) {
        cache.partnerDebts = debts;
        cache.partnerLastFetchTime = now;
      } else {
        cache.debts = debts;
        cache.lastFetchTime = now;
      }
      
      return debts;
    } catch (error) {
      console.error('Error fetching debts:', error);
      // If we have cached data, return it even if expired
      const fallbackCache = isPartnerMode ? cache.partnerDebts : cache.debts;
      if (fallbackCache) {
        console.log(`Using expired cached data for debts (${isPartnerMode ? 'partner' : 'personal'})`);
        return fallbackCache;
      }
      throw error;
    }
  },

  /**
   * Get a debt by its ID
   * @param {string} debtId - The debt ID to retrieve
   * @returns {Promise} Promise with debt data
   */
  async getDebtById(debtId) {
    try {
      // First check if we have this debt in our cache
      if (cache.debts) {
        const cachedDebt = cache.debts.find(debt => debt._id === debtId);
        if (cachedDebt) {
          return cachedDebt;
        }
      }
      
      const response = await axiosInstance.get(`/api/cashflow/debts/${debtId}`, this.getAuthHeader());
      return response.data;
    } catch (error) {
      console.error(`Error fetching debt ${debtId}:`, error);
      throw error;
    }
  },

  /**
   * Create a new debt
   * @param {Object} debtData - The debt data to create
   * @returns {Promise} Promise with created debt data
   */
  async createDebt(debtData) {
    try {
      const response = await axiosInstance.post(`/api/cashflow/debts`, debtData, this.getAuthHeader());
      
      // Invalidate cache
      cache.debts = null;
      cache.debtStats = null;
      
      return response.data;
    } catch (error) {
      console.error('Error creating debt:', error);
      throw error;
    }
  },

  /**
   * Update a debt
   * @param {string} debtId - The debt ID to update
   * @param {Object} debtData - The updated debt data
   * @returns {Promise} Promise with updated debt data
   */
  async updateDebt(debtId, debtData) {
    try {
      const response = await axiosInstance.put(
        `/api/cashflow/debts/${debtId}`, 
        debtData, 
        this.getAuthHeader()
      );
      
      // Invalidate cache
      cache.debts = null;
      cache.debtStats = null;
      
      return response.data;
    } catch (error) {
      console.error(`Error updating debt ${debtId}:`, error);
      throw error;
    }
  },

  /**
   * Delete a debt
   * @param {string} debtId - The debt ID to delete
   * @returns {Promise} Promise with deletion result
   */
  async deleteDebt(debtId) {
    try {
      const response = await axiosInstance.delete(
        `/api/cashflow/debts/${debtId}`, 
        this.getAuthHeader()
      );
      
      // Invalidate cache
      cache.debts = null;
      cache.debtStats = null;
      
      return response.data;
    } catch (error) {
      console.error(`Error deleting debt ${debtId}:`, error);
      throw error;
    }
  },

  /**
   * Record a payment towards a debt
   * @param {string} debtId - The debt ID
   * @param {Object} paymentData - Data about the payment
   * @returns {Promise} Promise with updated debt data
   */
  async recordPayment(debtId, paymentData) {
    try {
      const response = await axiosInstance.post(
        `/api/cashflow/debts/${debtId}/payments`,
        paymentData,
        this.getAuthHeader()
      );
      
      // Invalidate cache
      cache.debts = null;
      cache.debtStats = null;
      
      return response.data;
    } catch (error) {
      console.error(`Error recording payment to debt ${debtId}:`, error);
      throw error;
    }
  },

  /**
   * Clear cache to force fresh data fetch
   */
  clearCache() {
    cache.debts = null;
    cache.debtStats = null;
    cache.lastFetchTime = 0;
  },

  /**
   * Get wallet debt accounts (both manual and Plaid-connected)
   * @returns {Promise} Promise with wallet debt accounts data
   */
  async getWalletDebtAccounts() {
    try {
      console.log('🏦 Fetching wallet debt accounts...');
      const token = localStorage.getItem('token');
      
      if (!token) {
        console.error('❌ No authentication token found');
        return [];
      }

      // Use the specific wallet debt accounts endpoint
      const response = await axiosInstance.get('/api/cashflow/wallet-debts', {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.data.success) {
        console.log(`✅ Found ${response.data.data.length} wallet debt accounts`);
        return response.data.data;
      } else {
        console.error('❌ Failed to fetch wallet debt accounts:', response.data.message);
        return [];
      }
    } catch (error) {
      console.error('❌ Error fetching wallet debt accounts:', error);
      
      // If the endpoint fails, try to construct debt accounts from regular debt service + Plaid
      try {
        console.log('🔄 Fallback: Constructing debt accounts from multiple sources...');
        
        // Get manual debts
        const manualDebts = await this.getAllDebts();
        
        // Get Plaid debt accounts via wallet service
        const walletService = await import('../features/wallet/services/walletService.js');
        const plaidResponse = await walletService.default.getPlaidAccounts();
        
        let walletDebtAccounts = [];
        
        // Add manual debts (transform to match wallet account structure)
        if (Array.isArray(manualDebts)) {
          manualDebts.forEach(debt => {
            walletDebtAccounts.push({
              _id: debt._id,
              name: debt.name,
              accountType: debt.type === 'credit_card' ? 'Credit' : 'Loan',
              balance: debt.currentBalance || 0,
              interestRate: debt.interestRate || 0,
              minimumPayment: debt.minimumPayment || debt.monthlyPayment || 0,
              institutionName: debt.creditor || 'Manual Entry',
              isPlaidAccount: false,
              isManualDebt: true,
              debtDetails: {
                originalAmount: debt.originalAmount,
                startDate: debt.startDate,
                endDate: debt.endDate
              }
            });
          });
        }
        
        // Add Plaid debt accounts
        if (plaidResponse.success && Array.isArray(plaidResponse.data)) {
          plaidResponse.data.forEach(account => {
            const type = account.type?.toLowerCase() || '';
            const subtype = account.subtype?.toLowerCase() || '';
            
            // Filter for debt accounts
            if (type === 'loan' || type === 'credit' || subtype === 'loan' || subtype === 'credit card') {
              // Use proper balance field for credit cards vs loans with Chase anomaly detection
              const isCredit = type === 'credit';
              
              let actualDebt;
              if (isCredit) {
                // **CHASE ANOMALY DETECTION & FIX** (same logic as Wallet page)
                const hasChaseAnomaly = (
                  account.currentBalance === account.availableBalance && 
                  account.currentBalance > 0 &&
                  account.currentBalance >= 1000 // Likely credit limit, not debt
                );
                
                if (hasChaseAnomaly) {
                  console.log(`🔧 debtService - CHASE ANOMALY DETECTED:`, {
                    account: account.name || 'CREDIT CARD',
                    mask: account.mask,
                    reportedBalance: account.currentBalance,
                    availableBalance: account.availableBalance,
                    issue: 'currentBalance = availableBalance (likely credit limit misreported as debt)',
                    action: 'Treating as $0 debt - card appears paid off'
                  });
                  
                  actualDebt = 0; // Card is paid off
                } else {
                  actualDebt = Math.abs(account.currentBalance || 0); // Normal debt amount
                }
                
                console.log(`🏦 debtService: Credit card ${account.name} balance (debt): ${actualDebt}`, 
                  hasChaseAnomaly ? '(Chase anomaly corrected)' : `(currentBalance used: ${account.currentBalance})`);
              } else {
                // For loans, use balance or currentBalance
                actualDebt = Math.abs(account.currentBalance || account.balance || 0);
              }
              
              const debtAccount = {
                _id: account._id,
                name: account.name,
                accountType: type === 'credit' ? 'Credit' : 'Loan',
                balance: actualDebt,
                currentBalance: actualDebt,
                availableBalance: account.availableBalance || 0, // Credit limit for credit cards
                interestRate: account.loanDetails?.interestRate || account.interestRate || 0,
                minimumPayment: account.loanDetails?.paymentAmount || account.monthlyPayment || 0,
                monthlyPayment: account.loanDetails?.paymentAmount || account.monthlyPayment || 0,
                originalAmount: account.loanDetails?.originalPrincipal || account.originalAmount || 0,
                lastFourDigits: account.mask || '0000',
                institutionName: account.institutionName || 'Connected Bank',
                isPlaidAccount: true,
                // 🔥 CRITICAL FIX: Include asset category fields for filtering
                loanDetails: account.loanDetails || null,
                assetCategory: account.loanDetails?.assetCategory || account.assetCategory || null,
                termLength: account.loanDetails?.term || account.termLength || null,
                originatedDate: account.loanDetails?.originatedDate || account.originatedDate || null,
                plaidData: {
                  type: account.type,
                  subtype: account.subtype,
                  accountId: account.accountId
                }
              };
              
              // Add credit card specific fields for credit accounts
              if (isCredit) {
                if (account.lastStatementBalance !== undefined) {
                  debtAccount.lastStatementBalance = account.lastStatementBalance;
                }
                if (account.lastStatementDate !== undefined) {
                  debtAccount.lastStatementDate = account.lastStatementDate;
                }
                if (account.nextPaymentDue !== undefined) {
                  debtAccount.nextPaymentDue = account.nextPaymentDue;
                }
                if (account.lastPaymentAmount !== undefined) {
                  debtAccount.lastPaymentAmount = account.lastPaymentAmount;
                }
                if (account.lastPaymentDate !== undefined) {
                  debtAccount.lastPaymentDate = account.lastPaymentDate;
                }
                if (account.isOverdue !== undefined) {
                  debtAccount.isOverdue = account.isOverdue;
                }
              }
              
              walletDebtAccounts.push(debtAccount);
            }
          });
        }
        
        console.log(`✅ Fallback: Found ${walletDebtAccounts.length} total debt accounts`);
        return walletDebtAccounts;
        
      } catch (fallbackError) {
        console.error('❌ Fallback also failed:', fallbackError);
        return [];
      }
    }
  },

  /**
   * Get debt statistics for dashboard with caching
   * @returns {Promise} Promise with debt stats data
   */
  async getDebtStatistics(isPartnerMode = false, isOurMode = false) {
    try {
      // Check if we have cached stats and if they're still valid - use mode-specific cache
      const now = Date.now();
      let cachedStats, lastFetch;
      const mode = isOurMode ? 'our' : (isPartnerMode ? 'partner' : 'personal');
      
      if (isOurMode) {
        cachedStats = cache.ourDebtStats;
        lastFetch = cache.ourLastFetchTime;
      } else if (isPartnerMode) {
        cachedStats = cache.partnerDebtStats;
        lastFetch = cache.partnerLastFetchTime;
      } else {
        cachedStats = cache.debtStats;
        lastFetch = cache.lastFetchTime;
      }
      
      if (cachedStats && (now - lastFetch < cache.cacheExpiry)) {
        console.log(`Using cached debt stats (${mode})`);
        return cachedStats;
      }

      // Fetch all debts (this will use its own cache if available)
      let debts = await this.getAllDebts(isPartnerMode, isOurMode);
      if (!Array.isArray(debts)) debts = [];
      
      // Also fetch Plaid loan accounts to include in debt calculations
      let plaidDebtAccounts = [];
      try {
        const walletService = await import('../features/wallet/services/walletService.js');
        const plaidResponse = await walletService.default.getPlaidAccounts(isPartnerMode, isOurMode);
        if (plaidResponse.success && Array.isArray(plaidResponse.data)) {
          // Filter for loan and credit accounts
          plaidDebtAccounts = plaidResponse.data.filter(account => {
            const type = account.type?.toLowerCase() || '';
            const subtype = account.subtype?.toLowerCase() || '';
            return type === 'loan' || type === 'credit' || subtype === 'loan' || subtype === 'credit card';
          }).map(account => ({
            // Map Plaid account to debt-like structure
            _id: account._id,
            name: account.name,
            currentBalance: Math.abs(account.currentBalance || account.balance || 0),
            interestRate: account.loanDetails?.interestRate || account.interestRate || 0,
            minimumPayment: account.loanDetails?.paymentAmount || account.monthlyPayment || 0,
            monthlyPayment: account.loanDetails?.paymentAmount || account.monthlyPayment || 0,
            type: account.type === 'loan' ? 'loan' : 'credit_card',
            institutionName: account.institutionName || 'Connected Bank',
            isPlaidAccount: true
          }));
          
          console.log('🏦 Found Plaid debt accounts:', plaidDebtAccounts.length);
        }
      } catch (error) {
        console.error('Error fetching Plaid accounts for debt calculation:', error);
        // Continue without Plaid accounts if there's an error
      }
      
      // Combine regular debts and Plaid debt accounts
      const allDebts = [...debts, ...plaidDebtAccounts];
      
      if (allDebts.length === 0) {
        const emptyStats = {
          totalDebt: 0,
          monthlyPayment: 0,
          weightedInterestRate: 0,
          debtToIncomeRatio: 0,
          payoffTimeline: 0,
          highestInterestDebt: null,
          lowestInterestDebt: null
        };
        cache.debtStats = emptyStats;
        return emptyStats;
      }
      
      // Calculate total debt amount
      const totalDebt = allDebts.reduce((sum, debt) => sum + (debt.currentBalance || 0), 0);
      
      // Calculate monthly payment - FIXED to use minimumPayment which is what the server uses
      const monthlyPayment = allDebts.reduce((sum, debt) => {
        // Use minimumPayment field if available
        if (debt.minimumPayment) return sum + debt.minimumPayment;
        
        // Fallback to monthlyPayment if that exists
        if (debt.monthlyPayment) return sum + debt.monthlyPayment;
        
        // Otherwise, calculate a simple monthly payment based on remaining amount and term
        if (debt.currentBalance && debt.interestRate && debt.term) {
          const monthlyRate = (debt.interestRate / 100) / 12;
          const numPayments = debt.term;
          if (monthlyRate && numPayments) {
            const payment = debt.currentBalance * (monthlyRate * Math.pow(1 + monthlyRate, numPayments)) / 
                            (Math.pow(1 + monthlyRate, numPayments) - 1);
            return sum + payment;
          }
        }
        
        // As a last resort, estimate a simple payment (assume 10-year term and use the interest rate)
        if (debt.currentBalance && debt.interestRate) {
          const estimatedRate = (debt.interestRate / 100) / 12;
          const estimatedTerm = 120; // 10 years in months
          const estimatedPayment = debt.currentBalance * (estimatedRate * Math.pow(1 + estimatedRate, estimatedTerm)) /
                                   (Math.pow(1 + estimatedRate, estimatedTerm) - 1);
          return sum + estimatedPayment;
        }
        
        // If we can't calculate anything, just return the current sum
        return sum;
      }, 0);
      
      // Calculate weighted interest rate
      let weightedInterestSum = 0;
      allDebts.forEach(debt => {
        if (debt.currentBalance && debt.interestRate && totalDebt > 0) {
          weightedInterestSum += (debt.currentBalance / totalDebt) * debt.interestRate;
        }
      });
      
      // Calculate debt to income ratio (need income data for this)
      // For now we'll use a placeholder value of 5000 for monthly income
      const monthlyIncome = 5000; // This should be fetched from income service
      const debtToIncomeRatio = monthlyIncome > 0 ? (monthlyPayment / monthlyIncome) * 100 : 0;
      
      // Find highest and lowest interest debts
      let highestInterestDebt = null;
      let lowestInterestDebt = null;
      
      allDebts.forEach(debt => {
        if (debt.interestRate) {
          if (!highestInterestDebt || debt.interestRate > highestInterestDebt.interestRate) {
            highestInterestDebt = debt;
          }
          
          if (!lowestInterestDebt || debt.interestRate < lowestInterestDebt.interestRate) {
            lowestInterestDebt = debt;
          }
        }
      });
      
      // Calculate approximate payoff timeline in months (simple calculation)
      const payoffTimeline = monthlyPayment > 0 ? totalDebt / monthlyPayment : 0;
      
      const debtStats = {
        totalDebt,
        monthlyPayment,
        weightedInterestRate: weightedInterestSum,
        debtToIncomeRatio,
        payoffTimeline,
        highestInterestDebt,
        lowestInterestDebt
      };
      
      console.log('💳 Debt Stats Calculated:', {
        regularDebts: debts.length,
        plaidDebts: plaidDebtAccounts.length,
        totalDebt,
        monthlyPayment,
        weightedInterestRate: weightedInterestSum
      });
      
      // Update mode-specific cache
      if (isPartnerMode) {
        cache.partnerDebtStats = debtStats;
      } else {
        cache.debtStats = debtStats;
      }
      
      return debtStats;
    } catch (error) {
      console.error('Error fetching debt statistics:', error);
      
      // If we have cached stats, return them even if expired
      const fallbackStats = isPartnerMode ? cache.partnerDebtStats : cache.debtStats;
      if (fallbackStats) {
        console.log(`Using expired cached debt stats (${isPartnerMode ? 'partner' : 'personal'})`);
        return fallbackStats;
      }
      
      // Return default values as a last resort
      return {
        totalDebt: 0,
        monthlyPayment: 0,
        weightedInterestRate: 0,
        debtToIncomeRatio: 0,
        payoffTimeline: 0,
        highestInterestDebt: null,
        lowestInterestDebt: null
      };
    }
  }
};

export default debtService; 